import type { FetchedWasteTypes } from "@/Utils/api/sanity/types";
import { getUniqKeys } from "@/Utils/getUniqKeys";
import { sortByOrderRank } from "@/Utils/wasteStreams";
import type { RecyclingRatePoint } from "@/components/v2/TrendView/recyclingRate/types";
import type {
  WasteAmountsPoint,
  WasteAmountsStream,
} from "@/components/v2/TrendView/wasteAmounts/types";
import type { WasteTypeConfig } from "@/pages/configuration/useConfigService";
import {
  COLORS,
  getNewColor,
} from "@/pages/customer/dashboard/functions/pieChartData";

import { uniq } from "lodash";
import moment from "moment";
import { FilterTimeFrameValue } from "server/config-api/index.js";
import type { QuantityUnit, WasteType } from "server/dwhApi/common.js";
import type { GetTerminalAggregatesPeriodsResponse } from "server/dwhApi/terminals/models.js";
import type {
  TerminalAggregate,
  TerminalAggregatesPeriod,
} from "server/dwhApi/terminals/types.js";

export const getDataWithCurrentMonth = ({
  periodData,
  period,
}: {
  periodData?: GetTerminalAggregatesPeriodsResponse;
  period: FilterTimeFrameValue;
}) => {
  const data = [...(periodData?.periods || [])];

  const currentMonth = data[data.length - 1];
  if (currentMonth) {
    const totalOccurrences = currentMonth.terminalAggregates.totalOccurrences;

    if (totalOccurrences > 0) {
      if (period === FilterTimeFrameValue.Last12Months && data.length > 12) {
        data.shift();
      }
    } else {
      data.pop();
    }
  }

  return data;
};

export const getRecyclingRateGraphData = ({
  data,
  getPeriodLabelValue,
  comparisonYearBeforeData,
}: {
  data: TerminalAggregatesPeriod[];
  getPeriodLabelValue: (startTime: string) => string;
  comparisonYearBeforeData?: GetTerminalAggregatesPeriodsResponse;
}): RecyclingRatePoint[] => {
  return data.map((d) => {
    const point: RecyclingRatePoint = {
      label: getPeriodLabelValue(d.startTime),
      rate: d.terminalAggregates.sortingDegree * 100,
    };

    if (comparisonYearBeforeData) {
      const comparisonPeriod = comparisonYearBeforeData.periods.find(
        (p) =>
          moment(p.startTime).format("MMM") ===
          moment(d.startTime).format("MMM"),
      );

      if (comparisonPeriod) {
        point.prevYearRate =
          comparisonPeriod.terminalAggregates.sortingDegree * 100;
      }
    }

    return point;
  });
};

type WasteAmountsProps = {
  wasteTypes?: FetchedWasteTypes | null;
  wasteTypeConfig?: WasteTypeConfig[];
  getPeriodLabelValue: (startTime: string) => string;
};

const getBarData = ({
  terminalAggregate,
  startTime,
  wasteTypes,
  wasteTypeConfig,
  getPeriodLabelValue,
}: WasteAmountsProps & {
  terminalAggregate: TerminalAggregate;
  startTime: string;
}) => {
  const parsedWasteTypes = terminalAggregate?.wasteTypes?.reduce(
    (
      prev: any,
      { code: wasteCode, weight = {} as QuantityUnit }: WasteType,
    ) => {
      const wasteType = wasteTypes?.find((w) => w.id === wasteCode);

      const customName = wasteTypeConfig?.find(
        (wtc) => wtc.wasteTypeCode === wasteCode,
      )?.name;
      const wasteTypeName = `${customName || wasteType?.name || wasteCode} (${wasteCode})`;
      return {
        ...prev,
        total: prev.total + weight.quantity,
        [wasteTypeName]: weight.quantity,
      };
    },
    { total: 0 },
  );
  return { label: getPeriodLabelValue(startTime), ...parsedWasteTypes };
};

export const getWasteAmountsGraphData = (
  props: WasteAmountsProps & { data: TerminalAggregatesPeriod[] },
): WasteAmountsPoint[] =>
  props.data.map((d) =>
    getBarData({
      ...props,
      terminalAggregate: d.terminalAggregates,
      startTime: d.startTime,
    }),
  );

const getWasteCodes = ({
  data,
  wasteTypes,
}: {
  data: TerminalAggregatesPeriod[];
  wasteTypes: FetchedWasteTypes | null;
}) => {
  if (!data) return [];

  const types = uniq(
    data?.flatMap(
      ({ terminalAggregates }: { terminalAggregates: TerminalAggregate }) => {
        return (
          terminalAggregates?.wasteTypes?.map(({ code }: WasteType) => code) ??
          []
        );
      },
    ),
  );

  return types.map((wasteCode: string) => ({
    id: wasteCode,
    name: wasteTypes?.find((w) => w.id === wasteCode)?.name,
  }));
};

export const getWasteStreams = ({
  data,
  wasteAmountData,
  wasteTypes,
  wasteTypeConfig,
}: {
  data: TerminalAggregatesPeriod[];
  wasteAmountData: WasteAmountsPoint[];
  wasteTypes: FetchedWasteTypes | null;
  wasteTypeConfig?: WasteTypeConfig[];
}): WasteAmountsStream[] => {
  let curColors = [...COLORS];
  const wasteCodes = getWasteCodes({ data, wasteTypes });

  const streams = sortByOrderRank(
    getUniqKeys(wasteAmountData)
      .filter((item) => item !== "label" && item !== "total")
      .map((el) => {
        const [color, newColors] = getNewColor(curColors);
        curColors = newColors;

        const wasteCode = wasteCodes?.find((w) => el.includes(`(${w.id})`))?.id;
        const customColor = wasteTypeConfig?.find(
          (wtc) => wtc.name === el || wtc.wasteTypeCode === wasteCode,
        )?.color;

        const sanityWasteType = wasteTypes?.find((wt) => wt.id === wasteCode);
        const sanityDefaultColor = sanityWasteType?.wasteTypeDefaultColor;
        return {
          key: el,
          color: customColor || sanityDefaultColor || color,
          name: el,
          code: wasteCode,
        };
      }),
    wasteTypes || [],
    "code",
  );

  return streams;
};
